/**
 * MODULE: _shared/frontend-shell/src/hooks/billing/useShellRenewBanner.ts
 * PURPOSE: Drive AppLayout top renew banner from workspace AI quota assistant-status
 * DEPENDENCIES: useAssistantQuotaStatus, assistantQuotaGuidance dismiss helpers
 * PUBLIC_API: useShellRenewBanner, ShellRenewBannerState
 * SIDE_EFFECTS: sessionStorage dismiss keys; polls quota status
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: ~95 / 100 lines
 * RECENT: 2026-08-06 [2.2.2133] — Shell renew banner for tokens exhausted / running low
 */
import { useEffect, useMemo, useState } from 'react';

import {
  clearShellRenewExceededDismiss,
  dismissAssistantQuotaWarning,
  dismissShellRenewExceeded,
  isAssistantQuotaWarningDismissed,
  isShellRenewExceededDismissed,
  type AssistantQuotaSeverity,
} from '../../components/assistant/assistantQuotaGuidance';
import { useAssistantQuotaStatus } from '../assistant/useAssistantQuotaStatus';

export type ShellRenewBannerState = {
  visible: boolean;
  severity: AssistantQuotaSeverity;
  percentUsed?: number;
  dismiss: () => void;
};

export function useShellRenewBanner(enabled = true): ShellRenewBannerState {
  const { data } = useAssistantQuotaStatus(enabled);
  const [dismissedWarningKey, setDismissedWarningKey] = useState<number | null>(null);
  const [exceededDismissed, setExceededDismissed] = useState(() => isShellRenewExceededDismissed());

  const status = data?.ai_tokens?.status ?? 'ok';
  const percentUsed = data?.ai_tokens?.percent_used;
  const alertThreshold = data?.ai_tokens?.active_alert_threshold;

  useEffect(() => {
    if (status === 'ok' || status === 'warning' || status === 'critical') {
      clearShellRenewExceededDismiss();
      setExceededDismissed(false);
    }
  }, [status]);

  return useMemo(() => {
    if (!enabled || status === 'ok') {
      return {
        visible: false,
        severity: 'warning' as const,
        dismiss: () => undefined,
      };
    }

    if (status === 'exceeded') {
      if (exceededDismissed || isShellRenewExceededDismissed()) {
        return {
          visible: false,
          severity: 'exceeded' as const,
          percentUsed,
          dismiss: () => undefined,
        };
      }
      return {
        visible: true,
        severity: 'exceeded' as const,
        percentUsed,
        dismiss: () => {
          dismissShellRenewExceeded();
          setExceededDismissed(true);
        },
      };
    }

    const severity: AssistantQuotaSeverity = status === 'critical' ? 'critical' : 'warning';
    const dismissKey = alertThreshold ?? (severity === 'critical' ? 90 : 75);
    if (dismissedWarningKey === dismissKey || isAssistantQuotaWarningDismissed(dismissKey)) {
      return {
        visible: false,
        severity,
        percentUsed,
        dismiss: () => undefined,
      };
    }

    return {
      visible: true,
      severity,
      percentUsed,
      dismiss: () => {
        dismissAssistantQuotaWarning(dismissKey);
        setDismissedWarningKey(dismissKey);
      },
    };
  }, [enabled, status, percentUsed, alertThreshold, dismissedWarningKey, exceededDismissed]);
}
